
import { useFacetsStore, useSearchWizardStore, usePinia } from '#imports'
import { Component, Prop, Vue, Watch } from '~/utility/pu-class-decorator'
import { FacetCounts } from '~/apps/facets/domain/FacetCounts'
import { FacetGroupType } from '../../../../facets/domain/facetGroups'
import FilterAndSortFacetsGroupDefault from './FilterAndSortFacetsGroupDefault.vue'
import FilterAndSortFacetsGroupRanked from './FilterAndSortFacetsGroupRanked.vue'

type FacetsChanges = Array<[string, boolean]>

@Component({
  components: {
    'filter-and-sort-facets-group-default': FilterAndSortFacetsGroupDefault,
    'filter-and-sort-facets-group-ranked': FilterAndSortFacetsGroupRanked,
  },
  setup() {
    const facetsStore = useFacetsStore()
    const searchWizardStore = useSearchWizardStore(usePinia())
    return {
      facetsStore,
      searchWizardStore,
    }
  },
})
export default class FilterAndSortFacets extends Vue {
  facetsStore: ReturnType<typeof useFacetsStore>
  searchWizardStore: ReturnType<typeof useSearchWizardStore>

  @Prop({ default: () => [] })
    selectedFacets: string[]

  @Prop()
    facetCounts: FacetCounts

  get facetGroups() {
    return this.facetsStore.facetGroups || []
  }

  @Prop()
    initialFacetCounts: FacetCounts

  selectedPerGroup = {}

  facetsChange(changes: FacetsChanges) {
    this.$emit('changeFacets', this.recalculateSelectedFacets(changes))
  }

  getFacetGroupComponent(type: FacetGroupType) {
    return `filter-and-sort-facets-group-${type}`
  }

  get visibleGroups() {
    return this.facetGroups
      .map((group) => ({
        ...group,
        facets: group.hideEmpty
          ? group.facets.filter(
            (facet) =>
            // if we are not showing counts then show all
              !this.searchWizardStore.showCount ||
                // otherwise only show the facets that match initial counts
                this.initialFacetCounts[facet.slug] ||
                this.facetCounts[facet.slug],
          )
          : group.facets,
      }))
      .filter(
        (group) => group.facets.length > 0 && group.slug !== 'campsite-group',
      )
  }

  private recalculateSelectedFacets(changes: FacetsChanges) {
    const facetsRemoved: string[] = []
    const tmpFacets = [...this.selectedFacets]
    for (const [slug, isAdded] of changes) {
      isAdded ? tmpFacets.push(slug) : facetsRemoved.push(slug)
      this.updateSelectedCounts(slug, isAdded)
    }
    return tmpFacets.filter((slug) => !facetsRemoved.includes(slug))
  }

  @Watch('selectedFacets', { immediate: true })
  selectedFacetsChanged() {
    this.setupGroupCounts()
  }

  setupGroupCounts() {
    const selectedPerGroup = {}
    for (const group of this.visibleGroups) {
      selectedPerGroup[group.slug] = 0
    }
    this.selectedPerGroup = selectedPerGroup
    for (const facet of this.selectedFacets) {
      this.updateSelectedCounts(facet, true)
    }
  }

  updateSelectedCounts(selectedFacet, checked) {
    const parentGroupsForSelectedFacet = this.facetGroups
      .filter((slug) =>
        slug.facets.some((facet) => facet.slug === selectedFacet),
      )
      .map((facetGroups) => facetGroups.slug)
    for (const parentGroup of parentGroupsForSelectedFacet) {
      if (checked) {
        this.selectedPerGroup[parentGroup]++
      } else {
        this.selectedPerGroup[parentGroup]--
      }
    }
  }
}
